import { c as _c } from "react/compiler-runtime";
import request from '@onefootprint/request';
import type { GetEntityRequest, GetEntityResponse } from '@onefootprint/types';
import type { EntityStatus } from '@onefootprint/types/src/data';
import { useQueries } from '@tanstack/react-query';
import useSession, { type AuthHeaders } from '@/hooks/use-session';
import useGetEntityOwnedBusinessIds from './use-get-entity-owned-business-ids';
export type EntityOwnedBusinessInfo = {
  id: string;
  status: EntityStatus;
  name: string;
  startTimestamp: string;
};
export enum EntityOwnedBusinessRequestStatus {
  LOADING = 'LOADING',
  ERROR = 'ERROR',
  SUCCESS = 'SUCCESS',
}

// TODO: replace with the one from @onefootprint/fetch-dashboard
const getEntity = async (authHeaders: AuthHeaders, {
  id
}: GetEntityRequest) => {
  const response = await request<GetEntityResponse>({
    method: 'GET',
    url: `/entities/${id}`,
    headers: authHeaders
  });
  return response.data;
};
const useEntityOwnedBusinesses = id => {
  const $ = _c(23);
  const businessIdsMutation = useGetEntityOwnedBusinessIds(id);
  const businessIds = businessIdsMutation.data;
  const {
    authHeaders
  } = useSession();
  let t0;
  if ($[0] !== authHeaders || $[1] !== businessIds) {
    t0 = businessIds?.map(t1 => {
      const {
        id: bid
      } = t1;
      return {
        queryKey: ["entity", bid, authHeaders],
        queryFn: () => getEntity(authHeaders, {
          id: bid
        }),
        select: _temp2
      };
    }) || [];
    $[0] = authHeaders;
    $[1] = businessIds;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== t0) {
    t1 = {
      queries: t0
    };
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const businessQueries = useQueries(t1);
  let t2;
  if ($[5] !== businessIdsMutation.isPending || $[6] !== businessQueries) {
    t2 = businessIdsMutation.isPending || businessQueries.some(_temp3);
    $[5] = businessIdsMutation.isPending;
    $[6] = businessQueries;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const isPending = t2;
  let t3;
  if ($[8] !== businessIdsMutation.isError || $[9] !== businessQueries) {
    t3 = businessIdsMutation.isError || businessQueries.some(_temp4);
    $[8] = businessIdsMutation.isError;
    $[9] = businessQueries;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const isError = t3;
  let t4;
  if ($[11] !== businessIdsMutation.isSuccess || $[12] !== businessQueries) {
    t4 = businessIdsMutation.isSuccess && businessQueries.every(_temp5);
    $[11] = businessIdsMutation.isSuccess;
    $[12] = businessQueries;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const isSuccess = t4;
  let t5;
  if ($[14] !== businessQueries || $[15] !== isSuccess) {
    t5 = isSuccess ? businessQueries.map(_temp6) : undefined;
    $[14] = businessQueries;
    $[15] = isSuccess;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const businesses = t5;
  const hasBusinesses = isSuccess && !!businesses?.length && businesses.length > 0;
  let t6;
  if ($[17] !== businesses || $[18] !== hasBusinesses || $[19] !== isError || $[20] !== isPending || $[21] !== isSuccess) {
    t6 = {
      hasBusinesses,
      ownedBusinesses: businesses,
      isPending,
      isError,
      isSuccess
    };
    $[17] = businesses;
    $[18] = hasBusinesses;
    $[19] = isError;
    $[20] = isPending;
    $[21] = isSuccess;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  return t6;
};
export default useEntityOwnedBusinesses;
function _temp(attribute) {
  return attribute.identifier === "business.name";
}
function _temp2(response) {
  return {
    id: response.id,
    status: response.status,
    name: response.data.find(_temp)?.value as string || "-",
    startTimestamp: response.startTimestamp
  };
}
function _temp3(query) {
  return query.isPending;
}
function _temp4(query_0) {
  return query_0.isError;
}
function _temp5(query_1) {
  return query_1.isSuccess;
}
function _temp6(query_2) {
  return query_2.data as EntityOwnedBusinessInfo;
}